import React, { useEffect, useState } from "react";
import moment from "moment";
import { Select } from "antd";
import excelPng from "../../../assets/images/excel.png";
import { exportTableToExcel } from "../../../Helpers/Excel/export";
import { useSelector } from "react-redux";
import { TableHeader, TableCell } from "./TableUI";

const { Option } = Select;

const IrrigationTable = ({ data, dateRange }) => {
  const user = useSelector((state) => state.user.userInfo);
  const [sortedData, setSortedData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedSector, setSelectedSector] = useState("all");
  const [selectedValve, setSelectedValve] = useState("all");
  const [availableSectors, setAvailableSectors] = useState([]);
  const [availableValves, setAvailableValves] = useState([]);

  useEffect(() => { 
    if (data) {
      // Get unique sectors
      const sectors = [...new Set(data.map(item => item.sector_1))].sort((a, b) => a - b);
      setAvailableSectors(sectors);

      // Get unique valves
      const valves = [...new Set(data.map(item => {
        const res = [];
        for (let i = 1; i <= 12; i++) {
          if (item[`v${i}`] === 1) res.push(i);
        }
        return res;
      }).flat())].sort((a, b) => a - b);

      setAvailableValves(valves);
      
      // Filter data based on both sector and valve selections
      let filteredData = data;

      // Apply sector filter
      if (selectedSector !== "all") {
        filteredData = filteredData.filter(item => item.sector_1 === selectedSector);
      }

      // Apply valve filter
      if (selectedValve !== "all") {
        filteredData = filteredData.filter(item => item[`v${selectedValve}`] === 1);
      }

      setSortedData(filteredData);
    }
  }, [data, selectedSector, selectedValve]);

  const irrigationMode = (mode) => {
    switch (mode) {
      case "1": return "Auto";
      case "2": return "Semi-auto";
      case "3": return "Manuel";
      default: return "N/A";
    }
  };

  const fertilisationMode = (mode) => {
    switch (mode) {
      case "1": return "Basique";
      case "2": return "Quantitatif";
      case "3": return "Proportionnel";
      default: return "N/A";
    }
  };

  const asociatedValves = (item) => {
    const res = [];
    for (let i = 1; i <= 12; i++) {
      const valve = item[`v${i}`];
      if (Number(valve) === 1) res.push("V" + i);
    }

    // If no valve is open, delete the row
    if (res.length === 0) return null;

    return res.join("+ ");
  };

  const handleExportExcel = async () => {
    const filters = [
      "Irrigation par secteur",
      selectedSector === "all" ? "Tous les secteurs" : `Secteur ${selectedSector}`,
      selectedValve === "all" ? "Toutes les vannes" : `Vanne ${selectedValve}`,
      `Date à partir de : ${dateRange[0]?.format("DD/MM/YYYY")} `,
      `Date jusqu'au : ${dateRange[1]?.format("DD/MM/YYYY")}`,
    ];

    await exportTableToExcel(
      filters,
      ["irrigation-secteur"],
      "Irrigation-par-secteur",
      [],
      [],
      user.first_name + " " + user.last_name
    );
  };

  const sortData = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    const sorted = [...sortedData].sort((a, b) => {
      if (key === 'reception_date') {
        return direction === 'ascending'
          ? moment(a[key]).unix() - moment(b[key]).unix()
          : moment(b[key]).unix() - moment(a[key]).unix();
      }
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
    setSortConfig({ key, direction });
  };

  let stripCounter = 0;


  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="flex gap-8">
          <div>
          <span className=" font-semibold text-md">Filtrer par Secteur: </span>
          <Select
            style={{ width: 200 }}
            value={selectedSector}
            onChange={setSelectedSector}
            placeholder="Sélectionner un secteur"
          >
            <Option value="all">Tous les secteurs</Option>
            {availableSectors.map((sector) => (
              <Option key={sector} value={sector}>
                Secteur {sector}
              </Option>
            ))}
          </Select>
          </div>
          <div>
          <span className=" font-semibold text-md">Filtrer par Vanne: </span>
          <Select
            style={{ width: 200 }}
            value={selectedValve}
            onChange={setSelectedValve}
            placeholder="Sélectionner une vanne"
          >
            <Option value="all">Toutes les vannes</Option>
            {availableValves.map((valve) => (
              <Option key={valve} value={valve}>
                Vanne {valve}
              </Option>
            ))}
          </Select>
          </div>
        </div>

        <div
          className="flex gap-3 border p-2 hover:bg-green-200 rounded-md transition-all cursor-pointer"
          onClick={handleExportExcel}
        >
          <span className="font-bold text-lg">Export :</span>
          <img src={excelPng} alt="" className="w-8" />
        </div>
      </div>

      <table className="w-full table-auto border-collapse" id="irrigation-secteur">
        <thead>
          <tr>
            <th
              colSpan={5}
              className="text-2xl font-semibold p-3 bg-gray-700 text-white border-r-4 border-gray-500 table_heeader text-center"
            >
              Détails
            </th>
            <th
              colSpan={5}
              className="text-2xl font-semibold p-3 bg-gray-600 text-white border-r-4 border-gray-500 table_heeader text-center"
            >
              Irrigation
            </th>
            <th
              colSpan={10}
              className="text-2xl font-semibold p-3 bg-gray-500 text-white border-r-4 border-gray-500 table_heeader text-center"
            >
              Fertilisation
            </th>
          </tr>
          <tr>
            <TableHeader 
              title="Secteur" 
              backgroundColor="#4b5563" 
              onClick={() => sortData('sector_1')}
              isSortable={true}
            />
            <TableHeader title="Vannes" backgroundColor="#4b5563" />
            <TableHeader 
              title="Date de Réception" 
              backgroundColor="#4b5563" 
              onClick={() => sortData('reception_date')}
              isSortable={true}
            />
            <TableHeader title="Debut" backgroundColor="#374151" />
            <TableHeader title="Fin" backgroundColor="#374151" />
            <TableHeader title="Pression Consigne (bar)" backgroundColor="#374151" />
            <TableHeader title="Pression Moyenne (bar)" backgroundColor="#374151" />
            <TableHeader title="Mode Irrigation" backgroundColor="#374151" />
            <TableHeader title={`Volume (m³)`} backgroundColor="#374151" />
            <TableHeader title="Durée (min)" backgroundColor="#374151" />
            <TableHeader title="Fertilisation" backgroundColor="#1f2937" />
            <TableHeader title="pH Moyen" backgroundColor="#1f2937" />
            <TableHeader title="EC Moyen (mS/cm)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Acide (L)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Engrais 1 (L)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Engrais 2 (L)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Engrais 3 (L)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Engrais 4 (L)" backgroundColor="#1f2937" />
            <TableHeader title="Volume Engrais 5 (L)" backgroundColor="#1f2937" />
            <TableHeader title="Mode Fertilisation" backgroundColor="#1f2937" />
          </tr>
        </thead>
        <tbody>
          { 
          sortedData.map((item, index) => {
            if (!item || asociatedValves(item) === null) return null;
            stripCounter++;

            const isfertilisation = item.fertilisation;

            return (
            <tr
              key={index}
              style={{
                backgroundColor: stripCounter % 2 === 0 
                  ? "rgb(241, 242, 244)" 
                  : "rgba(255, 255, 255,1)",
              }}
            >
              <TableCell className="text-center">{item.sector_1}</TableCell>
              <TableCell className="text-center">{asociatedValves(item)}</TableCell>
              <TableCell className="text-center">
                {moment(item.reception_date).format("DD/MM/YYYY")}
              </TableCell>
              <TableCell className="text-center">
                {moment(item.reception_date).subtract(item.duration, "minutes").format("HH:mm")}
              </TableCell>
              <TableCell className="text-center">
                {moment(item.reception_date).format("HH:mm")}
              </TableCell>
              <TableCell className="text-right">{item.p_consigne / 100}</TableCell>
              <TableCell className="text-right">{item.p_moyenne}</TableCell>
              <TableCell className="text-center">{irrigationMode(item.irrigation_mode)}</TableCell>
              <TableCell className="text-right">{item.volume}</TableCell>
              <TableCell className="text-right">{item.duration}</TableCell>
              <TableCell className="text-center">{isfertilisation ? "Oui" : "Non"}</TableCell>
              <TableCell className={isfertilisation ? "text-right" : "text-center"}>{isfertilisation ? item.ph_moyenne : "---"}</TableCell>
              <TableCell className={isfertilisation ? "text-right" : "text-center"}>{isfertilisation ? item.ec_moyenne : "---"}</TableCell>
              <TableCell className="text-right">{item.volume_acide}</TableCell>
              <TableCell className="text-right">{item.volume_engrais_1}</TableCell>
              <TableCell className="text-right">{item.volume_engrais_2}</TableCell>
              <TableCell className="text-right">{item.volume_engrais_3}</TableCell>
              <TableCell className="text-right">{item.volume_engrais_4}</TableCell>
              <TableCell className="text-right">{item.volume_engrais_5}</TableCell>
              <TableCell className="text-center">{fertilisationMode(item.fertilisation_mode)}</TableCell>
            </tr>
)})}
        </tbody>
      </table>
    </>
  );
};

export default IrrigationTable;